import { useState } from "react";
import { toast } from "sonner";
import { CalendarBlank, PaperPlaneTilt } from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "../../api";
import { FadeIn } from "../FadeIn";

const INPUT_CLS =
  "w-full bg-transparent border border-white/15 px-4 py-3.5 text-sm text-white placeholder:text-white/30 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 rounded-none transition-colors";

export const Appointment = () => {
  const [form, setForm] = useState({ name: "", contact: "", when: "", reason: "" });
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/appointments", form);
      toast.success("Appointment request sent — we'll confirm with you directly.");
      setForm({ name: "", contact: "", when: "", reason: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="book" data-testid="appointment-section" className="border-t border-white/10 grid md:grid-cols-2">
      <FadeIn className="px-6 md:px-12 py-24 md:py-36 border-b md:border-b-0 md:border-r border-white/10">
        <p className="text-violet-400 text-xs tracking-[0.35em] uppercase mb-4 flex items-center gap-3">
          <CalendarBlank size={18} weight="duotone" /> Appointments
        </p>
        <h2 className="font-display uppercase tracking-tighter text-5xl md:text-7xl leading-[0.95]">
          Book a<br /><span className="text-outline-purple">visit</span>
        </h2>
        <p className="text-white/60 text-base md:text-lg mt-8 max-w-md">
          When you need us, get at us. We work for you. Tell us what's best for you
          and we will do what we can to make that work.
        </p>
        <div className="mt-10 space-y-3 text-sm text-white/50 uppercase tracking-widest">
          <p data-testid="appointment-phone">Text · 606-471-4369</p>
          <p data-testid="appointment-email">Email · Appguns@yahoo.com</p>
        </div>
      </FadeIn>

      <FadeIn delay={0.15} className="px-6 md:px-12 py-24 md:py-36">
        <form data-testid="appointment-form" onSubmit={submit} className="space-y-6">
          <div>
            <label className="block text-xs uppercase tracking-widest text-white/50 mb-2" htmlFor="appt-name">Name</label>
            <input id="appt-name" data-testid="appointment-name-input" required value={form.name} onChange={set("name")} placeholder="Your full name" className={INPUT_CLS} />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-widest text-white/50 mb-2" htmlFor="appt-contact">Email or Phone</label>
            <input id="appt-contact" data-testid="appointment-contact-input" required value={form.contact} onChange={set("contact")} placeholder="How do we confirm?" className={INPUT_CLS} />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-widest text-white/50 mb-2" htmlFor="appt-when">Preferred Date & Time</label>
            <input id="appt-when" data-testid="appointment-datetime-input" required type="datetime-local" value={form.when} onChange={set("when")} className={INPUT_CLS} />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-widest text-white/50 mb-2" htmlFor="appt-reason">Reason for Visit</label>
            <textarea id="appt-reason" data-testid="appointment-reason-input" required rows={4} value={form.reason} onChange={set("reason")} placeholder="Transfer, trade, appraisal..." className={INPUT_CLS} />
          </div>
          <button
            type="submit"
            data-testid="appointment-submit-btn"
            disabled={busy}
            className="w-full bg-primary text-white px-8 py-4 uppercase tracking-widest text-sm font-semibold hover:bg-[#FF7733] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {busy ? "Sending..." : "Request Appointment"} <PaperPlaneTilt weight="bold" />
          </button>
        </form>
      </FadeIn>
    </section>
  );
};
